/**
 * Grossissement animé du marqueur survolé.
 *
 * Le marqueur visé est recopié dans une source à un seul élément (`hov`), et
 * c'est cette copie qu'on anime. Animer les couches de base appliquerait la
 * taille aux 705 points à la fois : `icon-size` est une propriété de couche,
 * pas d'élément.
 *
 * Interpolation exponentielle plutôt que linéaire : le marqueur part vite et
 * s'installe, ce qui se lit comme une réponse au geste et non comme une
 * transition qui se déroule.
 */

import { CIRCLE_RADIUS, EMPHASIS_RATIO, SQUARE_SIZE, TRIANGLE_SIZE, evalAtZoom } from './sizing.js'

const EMPTY = { type: 'FeatureCollection', features: [] }

const TIME_CONSTANT = 70 // ms : plus petit = plus sec
const MAX_FRAME = 64 // ms : au-delà, l'onglet était en arrière-plan ; on ne rattrape pas
const SETTLED = 0.012 // en deçà, l'écart n'est plus visible

const stopsFor = (shape) => (shape === 't' ? TRIANGLE_SIZE : SQUARE_SIZE)

export function createHover(map, popup) {
  const state = { kind: null, id: null, shape: null, scale: 1, target: 1, raf: 0, lastTs: 0 }

  function applySize() {
    if (state.shape === null) return
    const zoom = map.getZoom()
    if (state.shape === 'c') {
      map.setPaintProperty('hov-c', 'circle-radius', evalAtZoom(zoom, CIRCLE_RADIUS) * state.scale)
    } else {
      map.setLayoutProperty('hov-s', 'icon-size', evalAtZoom(zoom, stopsFor(state.shape)) * state.scale)
    }
  }

  function clear() {
    state.kind = null
    state.id = null
    state.shape = null
    state.scale = 1
    try {
      map.getSource('hov')?.setData(EMPTY)
    } catch {
      // Style en cours de rechargement : la source réapparaîtra vide de toute façon.
    }
  }

  function tick(ts) {
    if (!state.lastTs) state.lastTs = ts
    const dt = Math.min(MAX_FRAME, ts - state.lastTs)
    state.lastTs = ts

    const delta = state.target - state.scale
    if (Math.abs(delta) < SETTLED) {
      state.scale = state.target
      applySize()
      state.raf = 0
      state.lastTs = 0
      if (state.target <= 1) clear() // retour à la taille normale : on retire la copie
      return
    }

    state.scale += delta * Math.min(1, dt / TIME_CONSTANT)
    applySize()
    state.raf = requestAnimationFrame(tick)
  }

  function start() {
    state.lastTs = 0
    if (!state.raf) state.raf = requestAnimationFrame(tick)
  }

  return {
    get current() {
      return state.id === null ? null : { kind: state.kind, id: state.id }
    },

    /** @param target {{kind, id, shape, status, coords, label}} */
    enter(target, { showPopup = true } = {}) {
      // Déjà sur ce marqueur : on relance vers la taille haute sans réinitialiser.
      if (state.id === target.id && state.kind === target.kind) {
        state.target = EMPHASIS_RATIO
        if (!state.raf && state.scale < EMPHASIS_RATIO) start()
        return
      }

      state.kind = target.kind
      state.id = target.id
      state.shape = target.shape
      state.scale = 1
      state.target = EMPHASIS_RATIO

      map.getSource('hov').setData({
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: { type: 'Point', coordinates: target.coords },
          properties: {
            shape: target.shape,
            status: target.status,
            image: `${target.shape === 't' ? 'tri' : 'sq'}-${target.status}`,
          },
        }],
      })

      applySize()
      if (showPopup) popup.setLngLat(target.coords).setHTML(target.labelHtml).addTo(map)
      else popup.remove()
      start()
    },

    leave() {
      state.target = 1
      popup.remove()
      start()
    },

    /** Le zoom change la taille de base : la copie animée doit suivre. */
    onZoom() {
      if (state.id !== null) applySize()
    },

    destroy() {
      if (state.raf) cancelAnimationFrame(state.raf)
      popup.remove()
    },
  }
}
